// src/components/forms/SignupForm.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { signUp, login } from '@/services/apiService';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { EmailVerificationModal } from '@/components/ui/EmailVerificationModal';
import { motion } from 'framer-motion';
import { useTenant } from '@/contexts/TenantContext';

export const SignUpForm = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [first, setFname] = useState('');
  const [last, setLname] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

  const navigate = useNavigate();
  const { tenant } = useTenant();
  const tenantSlug = tenant?.slug || 'app';
  const googleEnabled = tenant?.google_login_enabled !== false;


const handleGoogleLogin = () => {
  const apiBase = window.apiConfig?.apiBase || 'http://localhost:8000';
  const base = apiBase || 'http://localhost:8000';
  window.location.href = `${base}/api/auth/google/login/?tenant=${tenantSlug}`;
};


  /**
   * Extract validation errors from backend response.
   * Backend returns: { errors: { field: "message", ... } }
   */
const extractFieldErrors = (responseData) => {
    const errors = {};

    if (responseData?.errors && typeof responseData.errors === 'object') {
      Object.entries(responseData.errors).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          errors[field] = messages[0];
        } else {
          errors[field] = messages;
        }
      });
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    try {
      const data = {
        username,
        email,
        first_name: first,
        last_name: last,
        password,
      };

      await signUp(data);

      // 🔐 Auto-login immediately after signup
      await login(username, password);

      // 📧 Show verification modal (non-blocking)
      setVerificationEmail(email);
      setShowVerificationModal(true);

    } catch (err) {
      console.error('Signup error:', err);

      // ✅ Extract field-level validation errors from backend
      const backendErrors = extractFieldErrors(err?.response?.data);

      if (Object.keys(backendErrors).length > 0) {
        setFieldErrors(backendErrors);
        setError('Please fix the errors below.');
      } else {
        const errorMsg =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Registration failed. Please try again.';

        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowVerificationModal(false);
    navigate('/competitions');
  };

  return (
    <>
      <motion.form
        onSubmit={handleSubmit}
        className="flex flex-col gap-5"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* Error messages stay outside the input group */}
        {error && <p className="text-red-500 text-center">{error}</p>}
        {success && <p className="text-green-500 text-center">{success}</p>}

        {/* Group only the input fields together */}
        <div className="flex flex-col gap-2">
          <div>
            <Input
              type="text"
              placeholder="First Name"
              value={first}
              onChange={(e) => setFname(e.target.value)}
              required
              className={fieldErrors.first_name ? 'border-red-500' : ''}
            />
            {fieldErrors.first_name && (
              <p className="text-xs text-red-500 mt-1">{fieldErrors.first_name}</p>
            )}
          </div>

          <div>
            <Input
              type="text"
              placeholder="Last Name"
              value={last}
              onChange={(e) => setLname(e.target.value)}
              required
              className={fieldErrors.last_name ? 'border-red-500' : ''}
            />
            {fieldErrors.last_name && (
              <p className="text-xs text-red-500 mt-1">{fieldErrors.last_name}</p>
            )}
          </div>

          <div>
            <Input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className={fieldErrors.username ? 'border-red-500' : ''}
            />
            {fieldErrors.username && (
              <p className="text-xs text-red-500 mt-1">{fieldErrors.username}</p>
            )}
          </div>

          <div>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={fieldErrors.email ? 'border-red-500' : ''}
            />
            {fieldErrors.email && (
              <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>
            )}
          </div>

          <div>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={fieldErrors.password ? 'border-red-500' : ''}
            />
            {fieldErrors.password && (
              <p className="text-xs text-red-500 mt-1">{fieldErrors.password}</p>
            )}
          </div>
        </div>

        {/* Button stays OUTSIDE the input group */}
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? 'Signing up...' : 'Sign Up'}
        </Button>
      </motion.form>


{/* Divider */}
<div className="flex items-center gap-3 mt-2">
  <div className="flex-1 h-px bg-pebble-brown/30" />
  <span className="text-xs text-swan/40">or</span>
  <div className="flex-1 h-px bg-pebble-brown/30" />
</div>

{/* Google Sign Up */}
{googleEnabled && (
  <button
    type="button"
    onClick={handleGoogleLogin}
    className="w-full flex items-center justify-center gap-3 border border-pebble-brown/30 rounded-lg py-2.5 px-4 text-sm text-swan/80 hover:bg-pebble-brown/10 transition-colors"
  >
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
    Continue with Google
  </button>
)}

      {/* Show verification modal after successful signup (non-blocking) */}
      {showVerificationModal && (
        <EmailVerificationModal
          email={verificationEmail}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};